import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Send, Clock, Users, CalendarClock, XCircle, Euro, AlertTriangle, BarChart3, X, ShoppingBag, UtensilsCrossed, Star } from 'lucide-react';
import { adminApi } from '../../services/api';
import { useBranding } from '../../context/BrandingContext';
import { useVenue } from '../../context/VenueContext';
import { TemplateGrid } from '../components/broadcast/TemplateGrid';
import { WhatsAppPreview } from '../components/broadcast/WhatsAppPreview';
import { ComposeStep } from '../components/broadcast/ComposeStep';
import { ChannelBadges } from '../components/ChannelSelector';
import { PageTooltip } from '../components/TooltipSystem';

export default function AdminBroadcast() {
  const branding = useBranding();
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [customText, setCustomText] = useState('');
  const [ctaText, setCtaText] = useState('');
  const [selectedTiers, setSelectedTiers] = useState([]);
  const [segment, setSegment] = useState('');
  const [inactiveDays, setInactiveDays] = useState('30');
  const [newDays, setNewDays] = useState('7');
  const [minPoints, setMinPoints] = useState('');
  const [minVisits, setMinVisits] = useState('');
  const [spendingSegment, setSpendingSegment] = useState('');
  const [frequencySegment, setFrequencySegment] = useState('');
  const [preview, setPreview] = useState(null);
  const [frequency, setFrequency] = useState({ count: 0, limit: 4 });
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [attributionFor, setAttributionFor] = useState(null); // broadcast obj when modal open
  const [step, setStep] = useState('templates');
  const [showFilters, setShowFilters] = useState(false);
  const [formattedHours, setFormattedHours] = useState('');
  const [activeChallenges, setActiveChallenges] = useState([]);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [activePromos, setActivePromos] = useState([]);
  const [selectedPromo, setSelectedPromo] = useState(null);
  const [eventName, setEventName] = useState('');
  const [activeEvents, setActiveEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [scheduleMode, setScheduleMode] = useState('now');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('10:00');
  const [cancelling, setCancelling] = useState(null);
  const [frequencyAlert, setFrequencyAlert] = useState(null);
  const [activeCoupons, setActiveCoupons] = useState([]);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [selectedChannels, setSelectedChannels] = useState(['whatsapp']);
  const [pushSubscribers, setPushSubscribers] = useState(0);
  const [twilioStatus, setTwilioStatus] = useState(null);  // { whatsapp_configured, sms_configured }
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    // Use allSettled so a single failing endpoint doesn't break template rendering
    Promise.allSettled([
      adminApi.get('/broadcast/templates'),
      adminApi.get('/broadcast/frequency'),
      adminApi.get('/broadcasts'),
      adminApi.get('/business/hours'),
      adminApi.get('/broadcast/active-challenges'),
      adminApi.get('/broadcast/active-promotions'),
      adminApi.get('/broadcast/active-events'),
      adminApi.get('/broadcast/active-coupons'),
      adminApi.get('/broadcast/channel-costs'),
      adminApi.get('/admin/integrations'),
    ]).then(results => {
      const [tRes, fRes, hRes, hoursRes, chRes, promoRes, evRes, cpRes, chCosts, integRes] = results;
      if (tRes.status === 'fulfilled') setTemplates(tRes.value.data);
      if (fRes.status === 'fulfilled') setFrequency(fRes.value.data);
      if (hRes.status === 'fulfilled') setHistory(hRes.value.data);
      if (hoursRes.status === 'fulfilled') setFormattedHours(hoursRes.value.data?.formatted || '');
      if (chRes.status === 'fulfilled') setActiveChallenges(chRes.value.data || []);
      if (promoRes.status === 'fulfilled') setActivePromos(promoRes.value.data || []);
      if (evRes.status === 'fulfilled') setActiveEvents(evRes.value.data || []);
      if (cpRes.status === 'fulfilled') setActiveCoupons(cpRes.value.data || []);
      if (chCosts.status === 'fulfilled') setPushSubscribers(chCosts.value.data?.push_subscribers || 0);
      if (integRes.status === 'fulfilled') setTwilioStatus(integRes.value.data?.status || null);
    });
  }, []);

  const fetchPreview = useCallback(async () => {
    try {
      const body = { message: "preview" };
      if (selectedTiers.length) body.tier_filter = selectedTiers;
      if (minPoints) body.min_points = parseInt(minPoints);
      if (minVisits) body.min_visits = parseInt(minVisits);
      if (spendingSegment) body.spending_segment = spendingSegment;
      if (frequencySegment) body.frequency_segment = frequencySegment;
      if (segment) {
        body.segment = segment;
        if (segment === 'inactive') body.inactive_days = parseInt(inactiveDays) || 30;
        if (segment === 'new') body.new_days = parseInt(newDays) || 7;
      }
      const res = await adminApi.post('/broadcast/preview', body);
      setPreview(res.data.count);
      setFrequencyAlert(res.data.frequency_alert || null);
    } catch { setPreview(null); setFrequencyAlert(null); }
  }, [selectedTiers, minPoints, minVisits, segment, inactiveDays, newDays, spendingSegment, frequencySegment]);

  useEffect(() => {
    const t = setTimeout(fetchPreview, 300);
    return () => clearTimeout(t);
  }, [fetchPreview]);

  const selectTemplate = (t) => {
    setSelectedTemplate(t);
    setCustomText('');
    setCtaText(t.cta_suggestions?.[0] || '');
    setSelectedChallenge(null);
    setSelectedPromo(null);
    setSelectedCoupon(null);
    // For custom (free-text) templates default to Push (WhatsApp not allowed
    // because it requires a Meta-approved template). For all other templates
    // WhatsApp is the primary channel.
    setSelectedChannels(t.id === 'custom' ? ['push'] : ['whatsapp']);
    setEventName('');
    setStep('compose');
  };

  const renderPreviewMessage = () => {
    if (!selectedTemplate) return '';
    const biz = branding.business_name || 'Il tuo locale';
    if (selectedTemplate.id === 'custom') return (customText || '...').replace(/\{nome\}/g, 'Marco').replace(/\{locale\}/g, biz);
    if (selectedTemplate.auto_hours) return selectedTemplate.body.replace('{1}', 'Marco').replace('{2}', biz).replace('{3}', formattedHours || '(orari non configurati)');
    if (selectedTemplate.auto_challenge) {
      const ch = activeChallenges.find(c => c.id === selectedChallenge);
      return selectedTemplate.body.replace('{1}', 'Marco').replace('{2}', biz).replace('{3}', ch ? ch.description : '(seleziona una challenge)').replace('{4}', ch?.end_date || 'data da definire');
    }
    if (selectedTemplate.auto_promo) {
      const pr = activePromos.find(p => p.id === selectedPromo);
      return selectedTemplate.body.replace('{1}', 'Marco').replace('{2}', biz).replace('{3}', pr ? pr.description : '(seleziona una promozione)');
    }
    if (selectedTemplate.auto_menu) return selectedTemplate.body.replace('{1}', 'Marco').replace('{2}', biz).replace('{3}', customText || '(scrivi le novità del menu)');
    if (selectedTemplate.auto_evento) return selectedTemplate.body.replace('{1}', 'Marco').replace('{2}', eventName || '(nome evento)').replace('{3}', customText || '(dettagli evento)');
    if (selectedTemplate.auto_special_hours) return selectedTemplate.body.replace('{1}', 'Marco').replace('{2}', customText || '(scrivi gli orari speciali)');
    if (selectedTemplate.auto_coupon) {
      const cp = activeCoupons.find(c => c.id === selectedCoupon);
      return selectedTemplate.body.replace('{1}', 'Marco').replace('{2}', cp ? cp.code : '(codice)').replace('{3}', cp ? cp.reward_label : '(premio)').replace('{4}', cp?.valid_until || 'Senza scadenza');
    }
    return selectedTemplate.body.replace('{1}', 'Marco').replace('{2}', biz).replace('{3}', customText || '...').replace('{4}', ctaText || '');
  };

  const handleSend = async () => {
    if (!selectedTemplate) return;
    const { auto_hours, auto_challenge, auto_promo, auto_menu, auto_evento, auto_special_hours, auto_coupon } = selectedTemplate;
    if (!auto_hours && !auto_challenge && !auto_promo && !auto_evento && !auto_special_hours && !auto_coupon && !customText.trim()) { toast.error('Scrivi il testo del messaggio'); return; }
    if (auto_hours && !formattedHours) { toast.error('Configura prima gli orari nella sezione Locale'); return; }
    if (auto_challenge && !selectedChallenge) { toast.error('Seleziona una challenge'); return; }
    if (auto_promo && !selectedPromo) { toast.error('Seleziona una promozione'); return; }
    if (auto_menu && !customText.trim()) { toast.error('Scrivi le novità del menu'); return; }
    if (auto_evento && (!eventName?.trim() || !customText.trim())) { toast.error('Compila nome e dettagli evento'); return; }
    if (auto_special_hours && !customText.trim()) { toast.error('Scrivi gli orari speciali'); return; }
    if (auto_coupon && !selectedCoupon) { toast.error('Seleziona un coupon'); return; }
    if (!selectedChannels || selectedChannels.length === 0) { toast.error('Seleziona almeno un canale di invio'); return; }
    if (scheduleMode === 'scheduled') {
      if (!scheduledDate) { toast.error('Seleziona una data'); return; }
      if (!scheduledTime) { toast.error('Seleziona un orario'); return; }
    }
    // Open explicit confirmation so the admin always sees exactly which
    // channels will fire (prevents accidentally sending via WhatsApp when
    // only SMS was intended).
    setConfirmOpen(true);
  };

  const doSend = async () => {
    if (!selectedTemplate) return;
    const { auto_hours, auto_challenge, auto_promo, auto_menu, auto_evento, auto_special_hours, auto_coupon } = selectedTemplate;
    setConfirmOpen(false);
    setSending(true);
    try {
      const body = {
        template_id: selectedTemplate.id,
        custom_text: auto_hours ? 'auto_hours' : auto_challenge ? 'auto_challenge' : auto_promo ? 'auto_promo' : auto_coupon ? 'auto_coupon' : customText.trim(),
        cta_text: (auto_hours || auto_challenge || auto_promo || auto_menu || auto_evento || auto_special_hours || auto_coupon) ? '' : ctaText.trim(),
      };
      if (auto_challenge) body.challenge_id = selectedChallenge;
      if (auto_promo) body.promo_id = selectedPromo;
      if (auto_evento) body.event_name = eventName.trim();
      if (auto_coupon) body.coupon_id = selectedCoupon;
      body.channels = selectedChannels;
      if (selectedTiers.length) body.tier_filter = selectedTiers;
      if (minPoints) body.min_points = parseInt(minPoints);
      if (minVisits) body.min_visits = parseInt(minVisits);
      if (segment) {
        body.segment = segment;
        if (segment === 'inactive') body.inactive_days = parseInt(inactiveDays) || 30;
        if (segment === 'new') body.new_days = parseInt(newDays) || 7;
      }
      if (spendingSegment) body.spending_segment = spendingSegment;
      if (frequencySegment) body.frequency_segment = frequencySegment;
      if (scheduleMode === 'scheduled') body.scheduled_at = `${scheduledDate}T${scheduledTime}:00Z`;
      body.venue_id = localStorage.getItem('active_venue_id') || '';
      const res = await adminApi.post('/broadcast', body);
      toast.success(res.data.message);
      if (res.data.frequency_warning) toast.warning(res.data.frequency_warning, { duration: 6000 });
      setStep('templates'); setSelectedTemplate(null); setCustomText(''); setCtaText('');
      setScheduleMode('now'); setScheduledDate(''); setScheduledTime('10:00');
      const [fRes, hRes] = await Promise.all([adminApi.get('/broadcast/frequency'), adminApi.get('/broadcasts')]);
      setFrequency(fRes.data); setHistory(hRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nell'invio");
    } finally { setSending(false); }
  };

  const handleCancelScheduled = async (broadcastId) => {
    setCancelling(broadcastId);
    try {
      await adminApi.post(`/broadcast/${broadcastId}/cancel`);
      toast.success('Broadcast annullato');
      const [fRes, hRes] = await Promise.all([adminApi.get('/broadcast/frequency'), adminApi.get('/broadcasts')]);
      setFrequency(fRes.data); setHistory(hRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    } finally { setCancelling(null); }
  };

  const toggleTier = (t) => setSelectedTiers(p => p.includes(t) ? p.filter(x => x !== t) : [...p, t]);

  return (
    <div className="p-6 md:p-8 lg:p-10 max-w-6xl" data-testid="admin-broadcast-page">
      <div className="mb-5">
        <h1 className="text-2xl md:text-3xl tracking-tight font-bold text-[#1A1A1A]">Messaggi broadcast</h1>
        <p className="text-[#999] text-xs mt-0.5">Invia messaggi ai clienti tramite WhatsApp, SMS o notifiche push</p>
      </div>

      {/* Twilio not-configured warning. The broadcast page lists templates that
          will fail at send time without Twilio credentials, so we surface it
          early to avoid user frustration. */}
      {twilioStatus && !twilioStatus.whatsapp_configured && (
        <div className="mb-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3" data-testid="twilio-not-configured-banner">
          <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-xs font-semibold text-amber-900">WhatsApp non ancora configurato</p>
            <p className="text-[11px] text-amber-800 mt-0.5 leading-relaxed">
              I template approvati Meta non potranno partire fino a quando aggiungi le credenziali Twilio. Nel frattempo i broadcast verranno inviati solo via SMS o Push.
            </p>
            <Link to="/admin/integrations" data-testid="twilio-config-link"
              className="inline-block mt-1.5 text-[11px] font-semibold text-amber-700 hover:text-amber-900 underline-offset-2 hover:underline">
              Vai a Integrazioni →
            </Link>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2">
          {step === 'templates' ? (
            <>
              <TemplateGrid templates={templates} onSelect={selectTemplate} />

              {/* Scheduled Broadcasts Calendar */}
              <div className="mt-6" data-testid="scheduled-broadcasts-section">
                <div className="flex items-center gap-2 mb-3">
                  <CalendarClock size={15} className="text-[#D44E20]" />
                  <h2 className="font-semibold text-[#1A1A1A] text-sm">Broadcast programmati</h2>
                </div>
                {(() => {
                  const scheduled = history.filter(b => b.status === 'scheduled');
                  if (scheduled.length === 0) return (
                    <div className="bg-white rounded-2xl border border-dashed border-slate-200 p-6 text-center" data-testid="no-scheduled-broadcasts">
                      <CalendarClock size={28} className="mx-auto mb-2 text-[#ddd]" />
                      <p className="text-[#999] text-sm font-medium">Nessun broadcast programmato</p>
                      <p className="text-[#bbb] text-xs mt-1">Seleziona un template e programma il tuo prossimo messaggio!</p>
                    </div>
                  );
                  return (
                    <div className="space-y-2">
                      {scheduled.sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at)).map(b => (
                        <div key={b.id} className="bg-white rounded-2xl border border-blue-100 p-4 shadow-sm" data-testid={`scheduled-card-${b.id}`}>
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0"><CalendarClock size={16} className="text-blue-600" /></div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap mb-1">
                                {b.template_name && <span className="text-[9px] font-semibold text-[#D44E20] bg-[#D44E20]/8 px-2 py-0.5 rounded-md">{b.template_name}</span>}
                                <span className="text-[9px] font-bold text-blue-600 bg-blue-100 px-2 py-0.5 rounded-md">PROGRAMMATO</span>
                              </div>
                              <p className="text-[#333] text-xs line-clamp-2">{b.message}</p>
                              <div className="flex items-center gap-3 mt-2 flex-wrap">
                                <span className="text-xs text-blue-700 font-semibold flex items-center gap-1">
                                  <CalendarClock size={11} />
                                  {b.scheduled_at ? new Date(b.scheduled_at).toLocaleDateString('it-IT', { weekday: 'short', day: '2-digit', month: 'short' }) : ''}{' '}
                                  {b.scheduled_at ? new Date(b.scheduled_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }) : ''}
                                </span>
                                <span className="text-[10px] text-[#64748B] flex items-center gap-1"><Users size={9} /> {b.recipients_count} dest.</span>
                                {b.recipients_count > 0 && <span className="text-[10px] text-emerald-700 flex items-center gap-0.5"><Euro size={8} /> ~{(b.recipients_count * 0.0581).toFixed(2)}</span>}
                              </div>
                            </div>
                            <button onClick={() => handleCancelScheduled(b.id)} disabled={cancelling === b.id} data-testid={`cancel-scheduled-${b.id}`}
                              className="flex-shrink-0 px-3 py-1.5 border border-red-200 text-red-500 bg-red-50 text-[10px] font-medium rounded-lg flex items-center gap-1 hover:bg-red-100 transition-colors disabled:opacity-50">
                              {cancelling === b.id ? <div className="w-3 h-3 border border-red-400 border-t-transparent rounded-full animate-spin" /> : <XCircle size={10} />}
                              Annulla
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </>
          ) : (
            <ComposeStep
              template={selectedTemplate} customText={customText} setCustomText={setCustomText}
              ctaText={ctaText} setCtaText={setCtaText} segment={segment} setSegment={setSegment}
              selectedTiers={selectedTiers} toggleTier={toggleTier} setSelectedTiers={setSelectedTiers}
              minPoints={minPoints} setMinPoints={setMinPoints} minVisits={minVisits} setMinVisits={setMinVisits}
              inactiveDays={inactiveDays} setInactiveDays={setInactiveDays} newDays={newDays} setNewDays={setNewDays}
              spendingSegment={spendingSegment} setSpendingSegment={setSpendingSegment}
              frequencySegment={frequencySegment} setFrequencySegment={setFrequencySegment}
              showFilters={showFilters} setShowFilters={setShowFilters}
              preview={preview} sending={sending}
              onBack={() => { setStep('templates'); setSelectedTemplate(null); setSelectedChallenge(null); setSelectedPromo(null); setSelectedEvent(null); setSelectedCoupon(null); setEventName(''); }}
              onSend={handleSend}
              formattedHours={formattedHours}
              activeChallenges={activeChallenges} selectedChallenge={selectedChallenge} setSelectedChallenge={setSelectedChallenge}
              activePromos={activePromos} selectedPromo={selectedPromo} setSelectedPromo={setSelectedPromo}
              eventName={eventName} setEventName={setEventName}
              activeEvents={activeEvents} selectedEvent={selectedEvent} setSelectedEvent={setSelectedEvent}
              activeCoupons={activeCoupons} selectedCoupon={selectedCoupon} setSelectedCoupon={setSelectedCoupon}
              selectedChannels={selectedChannels} setSelectedChannels={setSelectedChannels} pushSubscribers={pushSubscribers}
              scheduleMode={scheduleMode} setScheduleMode={setScheduleMode}
              scheduledDate={scheduledDate} setScheduledDate={setScheduledDate}
              scheduledTime={scheduledTime} setScheduledTime={setScheduledTime}
              frequencyAlert={frequencyAlert}
            />
          )}
        </div>

        <div className="space-y-5">
          {step === 'compose' && selectedTemplate && <WhatsAppPreview message={renderPreviewMessage()} />}

          {/* History */}
          <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <Clock size={14} className="text-[#D44E20]" />
              <h2 className="font-semibold text-[#1A1A1A] text-sm">Storico invii</h2>
            </div>
            {history.length === 0 ? (
              <p className="text-[#999] text-xs text-center py-8">Nessun broadcast inviato</p>
            ) : (
              <div className="space-y-2 max-h-[360px] overflow-y-auto">
                {history.map(b => {
                  const status = b.status || 'sent';
                  const isScheduled = status === 'scheduled';
                  const isCancelled = status === 'cancelled';
                  return (
                    <div key={b.id} className={`rounded-xl p-3 border ${isCancelled ? 'bg-gray-50 border-slate-100 opacity-60' : isScheduled ? 'bg-blue-50/50 border-blue-100' : 'bg-gray-50 border-slate-100'}`} data-testid={`broadcast-history-${b.id}`}>
                      <PageTooltip page="broadcast" />
                      <div className="flex items-center gap-1.5 flex-wrap mb-1">
                        {b.template_name && <span className="text-[9px] font-semibold text-[#D44E20] bg-[#D44E20]/8 px-2 py-0.5 rounded-md">{b.template_name}</span>}
                        {isScheduled && <span className="text-[9px] font-bold text-blue-600 bg-blue-100 px-2 py-0.5 rounded-md flex items-center gap-0.5" data-testid={`badge-scheduled-${b.id}`}><CalendarClock size={8} /> PROGRAMMATO</span>}
                        {isCancelled && <span className="text-[9px] font-bold text-[#999] bg-gray-200 px-2 py-0.5 rounded-md" data-testid={`badge-cancelled-${b.id}`}>ANNULLATO</span>}
                        {!isScheduled && !isCancelled && <span className="text-[9px] font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-md">INVIATO</span>}
                      </div>
                      {b.channels && b.channels.length > 0 && <div className="mb-1"><ChannelBadges channels={b.channels} /></div>}
                      <p className="text-[11px] text-[#1A1A1A] line-clamp-2">{b.message}</p>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-[10px] text-[#999] flex items-center gap-1"><Users size={9} /> {b.recipients_count} dest.</span>
                        {b.recipients_count > 0 && <span className="text-[10px] text-emerald-700 flex items-center gap-0.5" data-testid={`history-cost-${b.id}`}><Euro size={8} /> ~{(b.recipients_count * 0.0581).toFixed(2)}</span>}
                        <div className="flex items-center gap-2">
                          {isScheduled && b.scheduled_at && <span className="text-[10px] text-blue-600 font-medium flex items-center gap-0.5"><CalendarClock size={9} />{new Date(b.scheduled_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })} {new Date(b.scheduled_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</span>}
                          {!isScheduled && <span className="text-[10px] text-[#999]">{b.created_at ? new Date(b.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</span>}
                        </div>
                      </div>
                      {isScheduled && (
                        <button onClick={() => handleCancelScheduled(b.id)} disabled={cancelling === b.id} data-testid={`cancel-broadcast-${b.id}`}
                          className="mt-2 w-full h-7 border border-red-200 text-red-500 bg-red-50 text-[10px] font-medium rounded-lg flex items-center justify-center gap-1 hover:bg-red-100 transition-colors disabled:opacity-50">
                          {cancelling === b.id ? <div className="w-3 h-3 border border-red-400 border-t-transparent rounded-full animate-spin" /> : <XCircle size={10} />}
                          {cancelling === b.id ? 'Annullamento...' : 'Annulla broadcast'}
                        </button>
                      )}
                      {!isScheduled && !isCancelled && b.recipients_count > 0 && (
                        <button onClick={() => setAttributionFor(b)} data-testid={`attribution-broadcast-${b.id}`}
                          className="mt-2 w-full h-7 border border-blue-200 text-blue-600 bg-blue-50/60 text-[10px] font-medium rounded-lg flex items-center justify-center gap-1 hover:bg-blue-100 transition-colors">
                          <BarChart3 size={10} /> Attribution clienti tornati
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {attributionFor && (
        <BroadcastAttributionModal
          broadcast={attributionFor}
          onClose={() => setAttributionFor(null)}
        />
      )}

      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="broadcast-confirm-modal" onClick={() => setConfirmOpen(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 rounded-xl bg-[#D44E20]/10 flex items-center justify-center"><Send size={16} className="text-[#D44E20]" /></div>
              <h3 className="font-bold text-[#1A1A1A] text-base">Conferma invio</h3>
            </div>
            <p className="text-sm text-[#64748B] mb-3">
              Stai per {scheduleMode === 'scheduled' ? 'programmare' : 'inviare'} questo messaggio
              {preview !== null ? <> a <span className="font-bold text-[#1A1A1A]">{preview} clienti</span></> : ''} tramite i canali:
            </p>
            <div className="flex flex-wrap gap-2 mb-3" data-testid="confirm-channel-badges">
              {selectedChannels.map(ch => {
                const meta = { whatsapp: { l: 'WhatsApp', c: '#25D366' }, sms: { l: 'SMS', c: '#F59E0B' }, push: { l: 'Push', c: '#6366F1' } }[ch] || { l: ch, c: '#64748B' };
                return (
                  <span key={ch} data-testid={`confirm-channel-${ch}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold" style={{ backgroundColor: `${meta.c}15`, color: meta.c }}>
                    {meta.l}
                  </span>
                );
              })}
            </div>
            {selectedChannels.includes('whatsapp') && (
              <div className="flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg mb-4" data-testid="confirm-whatsapp-notice">
                <AlertTriangle size={13} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-[11px] text-amber-800 leading-relaxed">
                  WhatsApp verrà inviato solo con template approvato da Meta. Se vuoi inviare <span className="font-semibold">solo SMS</span>, torna indietro e disattiva WhatsApp dai canali.
                </p>
              </div>
            )}
            <div className="flex gap-2">
              <button onClick={() => setConfirmOpen(false)} data-testid="confirm-cancel-btn"
                className="flex-1 h-10 rounded-xl border border-slate-200 text-[#64748B] text-sm font-medium hover:bg-slate-50 transition-colors">
                Annulla
              </button>
              <button onClick={doSend} data-testid="confirm-send-btn"
                className="flex-1 h-10 rounded-xl bg-[#D44E20] text-white text-sm font-bold flex items-center justify-center gap-1.5 hover:bg-[#C04018] transition-colors">
                <Send size={14} /> {scheduleMode === 'scheduled' ? 'Programma' : 'Invia ora'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Attribution modal: shows how many recipients of a broadcast came back
 * within 7 days (visit / food order / shop order). Filter by action type
 * to drill down.
 */
function BroadcastAttributionModal({ broadcast, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    setLoading(true);
    adminApi.get(`/admin/broadcasts/${broadcast.id}/attribution`)
      .then(r => setData(r.data))
      .catch(() => toast.error('Impossibile caricare l\'attribution'))
      .finally(() => setLoading(false));
  }, [broadcast.id]);

  const customers = (data?.customers || []).filter(c => filter === 'all' || c.action === filter);

  const ACTION_META = {
    visit: { label: 'Visita', icon: Star, color: '#D44E20' },
    food_order: { label: 'Ordine cibo', icon: UtensilsCrossed, color: '#10B981' },
    shop_order: { label: 'Acquisto shop', icon: ShoppingBag, color: '#3B82F6' },
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="attribution-modal">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h3 className="font-bold text-[#1A1A1A] text-base">Attribution: clienti tornati dopo broadcast</h3>
            <p className="text-xs text-[#64748B] mt-0.5">
              Template <span className="font-semibold">{broadcast.template_name || '—'}</span> · {data?.window_days || 7} giorni dall&apos;invio
            </p>
          </div>
          <button onClick={onClose} className="text-[#64748B] hover:text-[#1A1A1A]" data-testid="close-attribution-modal"><X size={18} /></button>
        </div>

        <div className="overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-12 text-[#64748B] text-sm">Caricamento…</div>
          ) : !data ? (
            <div className="text-center py-12 text-[#64748B] text-sm">Dati non disponibili</div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                <KpiBlock label="Destinatari" value={data.recipients_count} hint="che hanno ricevuto il messaggio" />
                <KpiBlock label="Tornati (7gg)" value={data.attributed.total} accent="#1A1A1A"
                  hint={`${data.attribution_rate}% del totale`} />
                <KpiBlock label="Visite" value={data.attributed.by_action.visit} accent="#D44E20" />
                <KpiBlock label="Ordini" value={(data.attributed.by_action.food_order || 0) + (data.attributed.by_action.shop_order || 0)} accent="#10B981"
                  hint={`${data.attributed.by_action.food_order || 0} cibo · ${data.attributed.by_action.shop_order || 0} shop`} />
              </div>

              <div className="flex gap-2 mb-3 flex-wrap" data-testid="attribution-filters">
                {[
                  { v: 'all', l: 'Tutti' },
                  { v: 'visit', l: 'Visite' },
                  { v: 'food_order', l: 'Ordini cibo' },
                  { v: 'shop_order', l: 'Acquisti shop' },
                ].map(f => (
                  <button key={f.v} onClick={() => setFilter(f.v)} data-testid={`attribution-filter-${f.v}`}
                    className={`px-3 py-1.5 rounded-xl text-xs font-semibold border transition-colors ${
                      filter === f.v ? 'bg-[#D44E20] text-white border-[#D44E20]' : 'bg-white text-[#64748B] border-slate-200 hover:bg-slate-50'
                    }`}>
                    {f.l}
                  </button>
                ))}
              </div>

              {customers.length === 0 ? (
                <div className="text-center py-12 rounded-xl bg-slate-50 border border-dashed border-slate-200 text-[#64748B] text-sm" data-testid="attribution-empty">
                  Nessuna azione attribuita per il filtro selezionato.
                </div>
              ) : (
                <div className="rounded-xl border border-slate-100 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-[#64748B] tracking-wider">Cliente</th>
                        <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-[#64748B] tracking-wider">Azione</th>
                        <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-[#64748B] tracking-wider">Quando</th>
                        <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase text-[#64748B] tracking-wider">Importo</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {customers.map((c, i) => {
                        const meta = ACTION_META[c.action] || { label: c.action, icon: Star, color: '#999' };
                        const Icon = meta.icon;
                        return (
                          <tr key={i} data-testid={`attribution-row-${i}`}>
                            <td className="px-3 py-2 text-[#1A1A1A] font-medium">{c.name}</td>
                            <td className="px-3 py-2">
                              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-semibold" style={{ backgroundColor: `${meta.color}15`, color: meta.color }}>
                                <Icon size={11} /> {meta.label}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-[#64748B] text-xs">
                              {c.occurred_at ? new Date(c.occurred_at).toLocaleString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums text-[#1A1A1A] text-xs">
                              {c.action === 'visit' ? `+${c.amount} pt` : c.amount ? `€ ${Number(c.amount).toFixed(2)}` : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function KpiBlock({ label, value, hint, accent }) {
  return (
    <div className="rounded-xl border border-slate-100 p-3 bg-white">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-[#94A3B8]">{label}</p>
      <p className="text-2xl font-bold tabular-nums mt-1" style={{ color: accent || '#D44E20' }}>{value ?? 0}</p>
      {hint && <p className="text-[10px] text-[#64748B] mt-0.5">{hint}</p>}
    </div>
  );
}
